import { ContainerComponent, wrap, today } from 'AppReact';
import { SecondaryTextXlBold, SecondaryTextBase } from 'atoms';
import Controller from './controller';
import {
  FlexCenterWrapper, Mt2, Mt6, AbsoluteLeftTopWrapper, BorderedCard,
  FlexCenterBetweenWrapper, W50, Mr2, Ml2, PrimaryBackground50Wrapper,
  P4, Px5, Pt5, Mt4
} from 'wrappers';
import internalState from './internalState';
import { Dropdown, LabeledDatepicker } from 'containers';
import AssignWorkerDropdown from '../assignWorkerDropdown';
import { calculateHoursBetweenDates, generateTimeArray, createPayLoadDateTime } from 'utils/dateAndTimeUtils';
import { OutlinedButton, SolidButton } from 'molecules';
import { format } from 'date-fns';
import TimeInput from '../timeInput';

const options = generateTimeArray();

export const definition = {
  name: 'TimeTrackerDropdown',
  controller: Controller,
  initialProps: ({ startTime, endTime, techs, vendors, assignedTechId, assignedVendorId, isEditing, assigneeName }) => ({
    techs, vendors, assignedTechId, assignedVendorId, isEditing, assigneeName,
    startTime: startTime ?? new Date(),
    endTime: endTime ?? new Date(),
    startH: format(startTime ?? new Date(), 'hh:mm aa'),
    endH: format(endTime ?? new Date(), 'hh:mm aa'),
  }),
  props: internalState,
  elements: [
    {
      wrapper: AbsoluteLeftTopWrapper,
      name: "DropdownWrapper",
      displayState: ({ isEditing }) => ({ top: isEditing ? '70px' : '34px' }),
      elements: [
        {
          wrapper: BorderedCard,
          name: 'Wrapper',
          displayState: ({ }) => ({ background: '#FFFFFF', borderRadius: '12px', padding: '0px' }),
          elements: [
            {
              component: wrap(wrap(SecondaryTextBase, Px5), Pt5),
              name: 'Start',
              displayValue: ({ }) => 'Start'
            },
            {
              wrapper: wrap(wrap(FlexCenterWrapper, Mt2), Px5),
              name: 'StartInputs',
              elements: [
                {
                  component: wrap(wrap(TimeInput, Mr2), W50),
                  name: 'TimeDropdown',
                  bindings: ({ startH, startTime, endH, endTime }) => ({ timeValue: startH, startH, startTime, endH, endTime })
                },
                {
                  component: wrap(wrap(LabeledDatepicker, Ml2), W50),
                  name: 'Calendar',
                  bindings: ({ startH, startTime, endH, endTime }) => ({
                    startH, startTime, endH, endTime,
                    enableFullWidth: true,
                    disabledAfter: today(),
                    selectedDate: startTime ?? new Date(),
                    alignRight: true,
                    label: {
                      values: [''],
                      keys: ['text'],
                      component: SecondaryTextBase
                    }
                  })
                }
              ]
            },
            {
              component: wrap(wrap(SecondaryTextBase, Mt6), Px5),
              name: 'End',
              displayValue: ({ }) => 'End'
            },
            {
              wrapper: wrap(wrap(FlexCenterWrapper, Mt2), Px5),
              name: 'EndInputs',
              elements: [
                {
                  component: wrap(wrap(TimeInput, Mr2), W50),
                  name: 'TimeDropdown',
                  bindings: ({ startH, startTime, endH, endTime }) => ({ timeValue: endH, startH, startTime, endH, endTime })
                },
                {
                  component: wrap(wrap(LabeledDatepicker, Ml2), W50),
                  name: 'Calendar',
                  bindings: ({ startH, startTime, endH, endTime }) => ({
                    startH, startTime, endH, endTime,
                    enableFullWidth: true,
                    disabledAfter: today(),
                    alignRight: true,
                    selectedDate: endTime ?? new Date(),
                    label: {
                      values: [''],
                      keys: ['text'],
                      component: SecondaryTextBase
                    }
                  })
                }
              ]
            },
            {
              component: wrap(wrap(SecondaryTextBase, Mt6), Px5),
              name: 'TimeLoggedFor',
              displayValue: ({ }) => 'Time logged for'
            },
            {
              component: wrap(wrap(AssignWorkerDropdown, Mt2), Px5),
              name: 'Assign',
              renderIf: ({ isEditing }) => !isEditing,
              bindings: ({ techs, vendors, assignedTechId }) => ({
                techs,
                vendors,
                admins: [],
                hideAdmins: true,
                assignedTechId,
                textHeader: false,
                hideVendors: true,
              }),
            },
            {
              component: wrap(wrap(SecondaryTextBase, Mt6), Px5),
              renderIf: ({ isEditing }) => isEditing,
              name: 'Assignee',
              displayValue: ({ assigneeName }) => assigneeName ?? '-'
            },
            {
              wrapper: wrap(wrap(FlexCenterBetweenWrapper, Mt6), Px5),
              name: 'TotalTime',
              elements: [
                {
                  component: SecondaryTextXlBold,
                  name: 'Total time',
                  displayValue: ({ }) => 'Total time'
                },
                {
                  component: SecondaryTextXlBold,
                  name: 'Time',
                  renderIf: ({ startH, endH, startTime, endTime }) => startH && endH && startTime && endTime,
                  displayValue: ({ startH, startTime, endH, endTime }) => {
                    let time = calculateHoursBetweenDates({ startDate: createPayLoadDateTime({ time: startH, date: startTime }), endDate: createPayLoadDateTime({ time: endH, date: endTime }), withSeconds: true })
                    return time?.total ?? '';
                  }
                },
              ]
            },
            {
              wrapper: wrap(wrap(wrap(FlexCenterBetweenWrapper, P4), PrimaryBackground50Wrapper), Mt4),
              name: 'ButtonsWrapper',
              elements: [
                {
                  component: SolidButton,
                  name: 'Save',
                  renderIf: ({ startH, endH, startTime, endTime }) => startH && endH && startTime && endTime,
                  bindings: ({ assignedTechId, startH, startTime, endH, endTime, isEditing }) => {
                    let time = calculateHoursBetweenDates({ startDate: createPayLoadDateTime({ time: startH, date: startTime }), endDate: createPayLoadDateTime({ time: endH, date: endTime }), withSeconds: true })
                    return {
                      title: 'Save',
                      big: true,
                      startDate: startH && startTime ? createPayLoadDateTime({ time: startH, date: startTime }) : new Date(),
                      endDate: endH && endTime ? createPayLoadDateTime({ time: endH, date: endTime }) : new Date(),
                      assignedTechId,
                      disabled: time.timeDifferenceInMinutes === 0 || (!isEditing && !assignedTechId)
                    }
                  },
                  displayState: ({ startH, startTime, endH, endTime, isEditing, assignedTechId }) => {
                    let time = calculateHoursBetweenDates({ startDate: createPayLoadDateTime({ time: startH, date: startTime }), endDate: createPayLoadDateTime({ time: endH, date: endTime }), withSeconds: true })
                    return { disabled: time.timeDifferenceInMinutes === 0 || (!isEditing && !assignedTechId) }
                  }
                },
                {
                  component: OutlinedButton,
                  name: 'Cancel',
                  bindings: () => ({ title: 'Cancel', big: true }),
                  displayState: () => ({ big: true })
                },
              ]
            }
          ]
        }
      ]
    },
  ]
}

export default ContainerComponent(definition);